import type { ThunkAction, UnknownAction } from '@reduxjs/toolkit';
import { baseApi } from '../../../core/baseApi';
import { createListQuery, createGetByIdQuery, createPaginatedListQuery, buildUrl, GetListArgs } from '../../../patterns/crud';
import { Contact, GetInfiniteContactsParams } from '../types';
import { CACHE_CONFIG } from '../../../core/cache';

/** Minimal shape of RTK Query internal cache state used for optimistic updates. */
interface RtkCacheEntry {
  data?: { data?: Contact[] } & Record<string, unknown>;
  originalArgs?: unknown;
}
type RtkQueryApiState = Record<string, { queries?: Record<string, RtkCacheEntry | undefined> }>;

/** RTK Query limitation: updateQueryData needs literal endpoint names for type inference.
 *  When a self-referencing API or dynamic endpoint names are used, the type can't be
 *  resolved automatically. This type wraps the assertion in one place. */
type PatchThunk = ThunkAction<{ undo: () => void }, unknown, unknown, UnknownAction>;
type DynamicPatchFn<T> = (
  endpointName: string,
  args: unknown,
  updateRecipe: (draft: T) => void
) => PatchThunk;

/**
 * Tracks contact IDs updated by the current client within the last few seconds.
 * Used by the socket event handler (useGlobalConversationEvents) to suppress
 * redundant cache invalidation that would race with the mutation's own
 * onQueryStarted patches and cause the UI to flash/revert.
 */
export const recentlyUpdatedContactIds = new Set<string>();

export const contactsCoreApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getAllContacts: createListQuery<Contact>('/contacts', 'Contacts')(builder),
    getContactDetails: createGetByIdQuery<Contact>('/contacts', 'Contacts')(builder),
    createContact: builder.mutation({
      query: (args) => ({
        url: '/contacts',
        method: 'POST',
        body: 'data' in args ? args.data : args
      }),
      transformResponse: (response: Record<string, unknown>) => response.data || response,
      invalidatesTags: (result, error) => {
        // If mutation succeeded, invalidate all contacts-related caches
        if (!error && result) {
          return [
            { type: 'Contacts', id: 'LIST' },
            { type: 'Contacts', id: 'PAGINATED-LIST' },
            { type: 'Contacts', id: 'INFINITE-LIST' },
            'ContactsCount',
            // Also invalidate conversations since they might show contact info
            { type: 'Conversations', id: 'LIST' },
            { type: 'Conversations', id: 'INFINITE-LIST' }
          ];
        }
        return [];
      }
    }),
    updateContact: builder.mutation({
      query: ({ id, data }) => ({
        url: `/contacts/${id}`,
        method: 'PUT',
        body: data
      }),
      transformResponse: (response: Record<string, unknown>) => response.data || response,
      async onQueryStarted({ id, data }, { dispatch, queryFulfilled, getState }) {
        // Optimistically patch the contact details cache
        // Component-level optimistic updates (ContactTagsDropdown, ContactRow) handle
        // immediate UI feedback. Cache patches here ensure data consistency.
        const patches: Array<{ undo: () => void }> = [];

        // Suppress socket-driven cache invalidation for this contact.
        // The backend emits a contact_edited socket event after the update which
        // triggers invalidateTags in useGlobalConversationEvents — that refetch
        // races with these patches and causes the UI to flash/revert.
        recentlyUpdatedContactIds.add(id);
        setTimeout(() => recentlyUpdatedContactIds.delete(id), 5000);

        // Helper to patch a contact in a list cache.
        // When firstName/lastName are being updated, also recomputes `fullName` so that
        // getContactDisplayName (which checks fullName first) immediately reflects the
        // change — without this, the display reverts to the old fullName until Phase 2.
        const patchListCache = (endpointName: string, originalArgs: unknown, updateData: Partial<Contact>) => {
          const patchList = (contactsCoreApi.util.updateQueryData as unknown as DynamicPatchFn<{ data?: Contact[] }>)(endpointName, originalArgs, (cache) => {
            if (cache?.data && Array.isArray(cache.data)) {
              const idx = cache.data.findIndex((c: Contact) => c.id === id);
              if (idx !== -1) {
                const existing = cache.data[idx];
                const patch: Partial<Contact> = { ...updateData };
                if ('firstName' in updateData || 'lastName' in updateData || 'preferredName' in updateData) {
                  const preferred = String((updateData.preferredName ?? existing.preferredName) || '');
                  const first = String((updateData.firstName ?? existing.firstName) || '');
                  const last = String((updateData.lastName ?? existing.lastName) || '');
                  patch.fullName = [preferred || first, last].filter(Boolean).join(' ');
                }
                cache.data[idx] = { ...existing, ...patch } as Contact;
              }
            }
          });
          patches.push(dispatch(patchList));
        };

        try {
          // Patch getContactDetails(id) - this cache key is stable (just the ID)
          const patchDetails = (contactsCoreApi.util.updateQueryData as unknown as DynamicPatchFn<Contact>)('getContactDetails', id, (draft) => {
            if (draft && data) {
              Object.assign(draft, data);
              if ('firstName' in data || 'lastName' in data || 'preferredName' in data) {
                const displayFirst = String(draft.preferredName || '') || String(draft.firstName || '');
                draft.fullName = [displayFirst, String(draft.lastName || '')].filter(Boolean).join(' ');
              }
            }
          });
          patches.push(dispatch(patchDetails));

          // For list caches (getInfiniteContacts, getPaginatedContacts), patch only the specific contact
          // This approach is safe for concurrent updates because each mutation only touches its own contact
           
          const state = getState() as RtkQueryApiState;
          const queries = state[baseApi.reducerPath]?.queries;
          if (queries) {
            Object.keys(queries).forEach(cacheKey => {
              if (cacheKey.startsWith('getInfiniteContacts-') || cacheKey.startsWith('getPaginatedContacts-')) {
                const cacheEntry = queries[cacheKey];
                if (cacheEntry?.data?.data && Array.isArray(cacheEntry.data.data)) {
                  const originalArgs = cacheEntry.originalArgs;
                  const endpointName = cacheKey.startsWith('getInfiniteContacts') ? 'getInfiniteContacts' : 'getPaginatedContacts';
                  patchListCache(endpointName, originalArgs, data as Partial<Contact>);
                }
              }
            });
          }

          // Await server confirmation and use response to update scalar fields.
          const { data: updatedContact } = await queryFulfilled;

          // Phase 2: patch caches with the server-confirmed values for scalar fields
          // (e.g. normalised phone, computed name). Exclude `folders`/`folderIds` because:
          //  a) Phase 1 already set them correctly from the optimistic payload, and
          //  b) the PUT endpoint typically does not eager-load the folders relation, so
          //     the server response may carry `folders: []` / `null` which would revert
          //     the tag update the user just performed (flash-and-disappear bug).
          if (updatedContact && typeof updatedContact === 'object') {
            const { folders: _f, folderIds: _fi, ...safeContactUpdate } = updatedContact as Partial<Contact> & { folderIds?: string[] };

            dispatch((contactsCoreApi.util.updateQueryData as unknown as DynamicPatchFn<Contact>)('getContactDetails', id, (draft) => {
              if (draft) Object.assign(draft, safeContactUpdate);
            }));

            const freshState = getState() as RtkQueryApiState;
            const freshQueries = freshState[baseApi.reducerPath]?.queries;
            if (freshQueries) {
              Object.keys(freshQueries).forEach(cacheKey => {
                if (cacheKey.startsWith('getInfiniteContacts-') || cacheKey.startsWith('getPaginatedContacts-')) {
                  const cacheEntry = freshQueries[cacheKey];
                  if (cacheEntry?.data?.data && Array.isArray(cacheEntry.data.data)) {
                    const originalArgs = cacheEntry.originalArgs;
                    const endpointName = cacheKey.startsWith('getInfiniteContacts') ? 'getInfiniteContacts' : 'getPaginatedContacts';
                    dispatch((contactsCoreApi.util.updateQueryData as unknown as DynamicPatchFn<{ data?: Contact[] }>)(endpointName, originalArgs, (cache) => {
                      if (cache?.data && Array.isArray(cache.data)) {
                        const idx = cache.data.findIndex((c: Contact) => c.id === id);
                        if (idx !== -1) {
                          cache.data[idx] = { ...cache.data[idx], ...safeContactUpdate } as Contact;
                        }
                      }
                    }));
                  }
                }
              });
            }
          }
        } catch {
          // Revert only the optimistic patches on error - not the server response patches
          patches.forEach(p => p.undo());
        }
      },
      // Optimistic updates in onQueryStarted handle all contact cache changes.
      // Do NOT invalidate { type: 'Contacts', id } on success — it triggers a
      // redundant getInfiniteContacts refetch that races with optimistic patches,
      // causing tag updates to flash and revert visually.
      // Only invalidate conversations (embedded contact name/avatar) on success,
      // and broad contact lists on error so they refetch after optimistic patches revert.
      invalidatesTags: (result, error, { id }) => {
        const tags: Array<{ type: 'Contacts' | 'Conversations'; id: string }> = [
          { type: 'Conversations', id: 'LIST' },
          { type: 'Conversations', id: 'INFINITE-LIST' },
        ];
        if (error) {
          tags.push(
            { type: 'Contacts', id },
            { type: 'Contacts', id: 'LIST' },
            { type: 'Contacts', id: 'PAGINATED-LIST' },
            { type: 'Contacts', id: 'INFINITE-LIST' },
          );
        }
        return tags;
      }
    }),
    deleteContact: builder.mutation<void, string>({
      query: (id: string) => ({
        url: `/contacts/${id}`,
        method: 'DELETE'
      }),
      // Delete endpoint doesn't return meaningful data
      transformResponse: () => undefined,
      async onQueryStarted(id, { dispatch, queryFulfilled, getState }) {
        // Optimistically remove the contact from all list caches so the row
        // disappears immediately.  The merge function in getInfiniteContacts
        // can't reliably remove contacts during a refetch (it preserves
        // existing entries from pages not included in the new response).
        const patches: Array<{ undo: () => void }> = [];

        // Suppress socket-driven cache invalidation for this contact
        recentlyUpdatedContactIds.add(id);
        setTimeout(() => recentlyUpdatedContactIds.delete(id), 5000);

        const state = getState() as RtkQueryApiState;
        const queries = state[baseApi.reducerPath]?.queries;
        if (queries) {
          Object.keys(queries).forEach(cacheKey => {
            if (cacheKey.startsWith('getInfiniteContacts-') || cacheKey.startsWith('getPaginatedContacts-')) {
              const cacheEntry = queries[cacheKey];
              if (cacheEntry?.data?.data && Array.isArray(cacheEntry.data.data)) {
                const originalArgs = cacheEntry.originalArgs;
                const endpointName = cacheKey.startsWith('getInfiniteContacts') ? 'getInfiniteContacts' : 'getPaginatedContacts';

                const patchList = (contactsCoreApi.util.updateQueryData as unknown as DynamicPatchFn<{ data?: Contact[] }>)(endpointName, originalArgs, (cache) => {
                  if (cache?.data && Array.isArray(cache.data)) {
                    const idx = cache.data.findIndex((c: Contact) => c.id === id);
                    if (idx !== -1) {
                      cache.data.splice(idx, 1);
                    }
                  }
                });
                patches.push(dispatch(patchList));
              }
            }
          });
        }

        try {
          await queryFulfilled;
        } catch {
          // Revert optimistic removal on error
          patches.forEach(p => p.undo());
        }
      },
      invalidatesTags: (result, error, id) => {
        // Invalidate all contact-related caches to ensure UI updates immediately
        if (!error) {
          return [
            { type: 'Contacts', id },
            { type: 'Contacts', id: 'LIST' },
            { type: 'Contacts', id: 'PAGINATED-LIST' },
            { type: 'Contacts', id: 'INFINITE-LIST' },
            'ContactsCount',
            // Also invalidate conversations since they might show contact info
            { type: 'Conversations', id: 'LIST' },
            { type: 'Conversations', id: 'INFINITE-LIST' }
          ];
        }
        return [];
      }
    }),
    getContactsCount: builder.query<{ total: number }, GetListArgs>({
      query: (params) => buildUrl('/contacts/count', params),
      transformResponse: (response: Record<string, unknown>) => (response.data || response) as { total: number },
      providesTags: ['ContactsCount'],
      keepUnusedDataFor: CACHE_CONFIG.CONTACTS.keepUnusedDataFor,
    }),
    syncExternalContacts: builder.mutation({
      query: (args: unknown) => ({
        url: '/contacts/sync',
        method: 'POST' as const,
        body: args && typeof args === 'object' && 'data' in args ? (args as { data: unknown }).data : args
      }),
      invalidatesTags: [
        { type: 'Contacts', id: 'LIST' },
        { type: 'Contacts', id: 'PAGINATED-LIST' },
        { type: 'Contacts', id: 'INFINITE-LIST' },
        'ContactsCount',
      ]
    }),
    getPaginatedContacts: createPaginatedListQuery<Contact>('/contacts', 'Contacts')(builder),
    getInfiniteContacts: builder.query<{ data: Contact[], total: number, hasMore: boolean, nextCursor?: string }, GetInfiniteContactsParams>({
      query: (params) => {
        const searchParams = new URLSearchParams();
        Object.entries(params || {}).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            if (typeof value === 'object') {
              searchParams.append(key, JSON.stringify(value));
            } else {
              searchParams.append(key, String(value));
            }
          }
        });
        const queryString = searchParams.toString();
        return queryString ? `/contacts?${queryString}` : '/contacts';
      },
      transformResponse: (response: { data?: { data?: Contact[]; total?: number; hasMore?: boolean; nextCursor?: string } | Contact[]; total?: number; hasMore?: boolean; nextCursor?: string }) => {
        const data = (response?.data && 'data' in response.data ? response.data.data : response?.data) || [];
        const contactsArray = Array.isArray(data) ? data : [];
        return {
          data: contactsArray as Contact[],
          total: (response?.data && typeof response.data === 'object' && 'total' in response.data ? response.data.total : response?.total) || 0,
          hasMore: (response?.data && typeof response.data === 'object' && 'hasMore' in response.data ? response.data.hasMore : response?.hasMore) || false,
          nextCursor: (response?.data && typeof response.data === 'object' && 'nextCursor' in response.data ? response.data.nextCursor : response?.nextCursor)
        };
      },
      // Include filter/sort params in cache key so different filters have separate caches
      // This prevents race conditions when switching filters
      serializeQueryArgs: ({ queryArgs, endpointName }) => {
        const { cursor: _cursor, ...paramsWithoutCursor } = queryArgs || {};
        return `${endpointName}-${JSON.stringify(paramsWithoutCursor)}`;
      },
      merge: (currentCache, newItems, { arg }) => {
        // If cursor is undefined, it's a fresh query (reset) — replace entirely.
        if (!arg?.cursor) {
          return newItems;
        }
        // Pagination append: ONLY add genuinely new contacts.
        // Do NOT overwrite existing contacts with paginated data — the page fetch
        // may have been initiated before an in-flight optimistic mutation committed,
        // so `fresh.folders` (or other fields) would be stale and would revert
        // any optimistic updates the user just made (tag flash-and-disappear bug).
        const existingIds = new Set((currentCache?.data || []).map(c => c.id));
        const genuinelyNew = newItems.data.filter(c => !existingIds.has(c.id));
        return {
          ...newItems,
          data: [...(currentCache?.data || []), ...genuinelyNew]
        };
      },
      forceRefetch: ({ currentArg, previousArg }) => {
        // Always refetch when cursor changes (pagination to next page)
        if (currentArg?.cursor !== previousArg?.cursor) {
          return true;
        }
        // Also refetch when filter/sort params change
        const { cursor: _currentCursor, ...currentRest } = currentArg || {};
        const { cursor: _prevCursor, ...prevRest } = previousArg || {};
        const sortedStringify = (obj: Record<string, unknown>) => JSON.stringify(obj, Object.keys(obj).sort());
        return sortedStringify(currentRest) !== sortedStringify(prevRest);
      },
      providesTags: (result) =>
        result?.data
          ? [
              { type: 'Contacts' as const, id: 'INFINITE-LIST' },
              ...result.data.map(item => ({ type: 'Contacts' as const, id: item.id }))
            ]
          : [{ type: 'Contacts' as const, id: 'INFINITE-LIST' }],
      // CRITICAL: Enable refetch on mount when cache is invalidated
      keepUnusedDataFor: 60, // Keep cache for 60 seconds
    }),
    getContactByConversation: builder.query<unknown, string>({
      query: (conversationId) => `/contacts/by-conversation/${conversationId}`,
      transformResponse: (response: Record<string, unknown>) => response.data || response,
      providesTags: (result, error, id) => [{ type: 'Contacts', id: `conv-${id}` }]
    }),
    blockContact: builder.mutation({
      query: (id: string) => ({
        url: `/contacts/${id}/block`,
        method: 'PUT'
      }),
      transformResponse: (response: Record<string, unknown>) => response.data || response,
      onQueryStarted: async (id, { dispatch, queryFulfilled, getState }) => {
        const patches: Array<{ undo: () => void }> = [];

        // Optimistic update for getContactDetails
        patches.push(dispatch(
          (contactsCoreApi.util.updateQueryData as unknown as DynamicPatchFn<Contact>)('getContactDetails', id, (draft) => {
            if (draft) {
              draft.isBlocked = true;
            }
          })
        ));

        // Also update all getInfiniteContacts cache entries

        const blockQueries = (getState() as RtkQueryApiState)[baseApi.reducerPath]?.queries;
        if (blockQueries) {
          Object.keys(blockQueries).forEach(cacheKey => {
            if (cacheKey.startsWith('getInfiniteContacts-') || cacheKey.startsWith('getPaginatedContacts-')) {
              const cacheEntry = blockQueries[cacheKey];
              if (cacheEntry?.data?.data && Array.isArray(cacheEntry.data.data)) {
                const originalArgs = cacheEntry.originalArgs;
                const endpointName = cacheKey.startsWith('getInfiniteContacts') ? 'getInfiniteContacts' : 'getPaginatedContacts';

                const patchList = (contactsCoreApi.util.updateQueryData as unknown as DynamicPatchFn<{ data?: Contact[] }>)(endpointName, originalArgs, (cache) => {
                  if (cache?.data && Array.isArray(cache.data)) {
                    const idx = cache.data.findIndex((c: Contact) => c.id === id);
                    if (idx !== -1) {
                      cache.data[idx] = { ...cache.data[idx], isBlocked: true };
                    }
                  }
                });
                patches.push(dispatch(patchList));
              }
            }
          });
        }

        try {
          await queryFulfilled;
        } catch {
          // Revert all patches on error
          patches.forEach(p => p.undo());
        }
      },
      invalidatesTags: (result, error, id) => [
        { type: 'Contacts', id },
        { type: 'Contacts', id: 'LIST' },
        { type: 'Conversations', id: 'LIST' },
        { type: 'Conversations', id: 'INFINITE-LIST' }
      ]
    }),
    changeConsent: builder.mutation<unknown, { id: string; action: 'grant' | 'revoke'; source: string; justification?: string }>({
      query: ({ id, action, source, justification }) => ({
        url: `/contacts/${id}/consent`,
        method: 'POST',
        body: { action, source, ...(justification ? { justification } : {}) }
      }),
      transformResponse: (response: Record<string, unknown>) => response.data || response,
      onQueryStarted: async ({ id, action }, { dispatch, queryFulfilled, getState }) => {
        const patches: Array<{ undo: () => void }> = [];

        const newConsentStatus = action === 'grant' ? 'consented' : 'opted_out';

        // Optimistic update for getContactDetails
        patches.push(dispatch(
          (contactsCoreApi.util.updateQueryData as unknown as DynamicPatchFn<Contact>)('getContactDetails', id, (draft) => {
            if (draft) {
              draft.consentStatus = newConsentStatus;
              draft.hasConsent = action === 'grant';
            }
          })
        ));

        // Also update all list cache entries
        const consentQueries = (getState() as RtkQueryApiState)[baseApi.reducerPath]?.queries;
        if (consentQueries) {
          Object.keys(consentQueries).forEach(cacheKey => {
            if (cacheKey.startsWith('getInfiniteContacts-') || cacheKey.startsWith('getPaginatedContacts-')) {
              const cacheEntry = consentQueries[cacheKey];
              if (cacheEntry?.data?.data && Array.isArray(cacheEntry.data.data)) {
                const originalArgs = cacheEntry.originalArgs;
                const endpointName = cacheKey.startsWith('getInfiniteContacts') ? 'getInfiniteContacts' : 'getPaginatedContacts';

                const patchList = (contactsCoreApi.util.updateQueryData as unknown as DynamicPatchFn<{ data?: Contact[] }>)(endpointName, originalArgs, (cache) => {
                  if (cache?.data && Array.isArray(cache.data)) {
                    const idx = cache.data.findIndex((c: Contact) => c.id === id);
                    if (idx !== -1) {
                      cache.data[idx] = { ...cache.data[idx], consentStatus: newConsentStatus, hasConsent: action === 'grant' };
                    }
                  }
                });
                patches.push(dispatch(patchList));
              }
            }
          });
        }

        try {
          await queryFulfilled;
        } catch {
          // Revert all patches on error
          patches.forEach(p => p.undo());
        }
      },
      invalidatesTags: (result, error, { id }) => [
        { type: 'Contacts', id },
        { type: 'Contacts', id: 'LIST' },
        { type: 'Conversations', id: 'LIST' },
        { type: 'Conversations', id: 'INFINITE-LIST' }
      ]
    }),
    unblockContact: builder.mutation({
      query: (id: string) => ({
        url: `/contacts/${id}/unblock`,
        method: 'PUT'
      }),
      transformResponse: (response: Record<string, unknown>) => response.data || response,
      onQueryStarted: async (id, { dispatch, queryFulfilled, getState }) => {
        const patches: Array<{ undo: () => void }> = [];

        // Optimistic update for getContactDetails
        patches.push(dispatch(
          (contactsCoreApi.util.updateQueryData as unknown as DynamicPatchFn<Contact>)('getContactDetails', id, (draft) => {
            if (draft) {
              draft.isBlocked = false;
            }
          })
        ));

        // Also update all getInfiniteContacts cache entries

        const unblockQueries = (getState() as RtkQueryApiState)[baseApi.reducerPath]?.queries;
        if (unblockQueries) {
          Object.keys(unblockQueries).forEach(cacheKey => {
            if (cacheKey.startsWith('getInfiniteContacts-') || cacheKey.startsWith('getPaginatedContacts-')) {
              const cacheEntry = unblockQueries[cacheKey];
              if (cacheEntry?.data?.data && Array.isArray(cacheEntry.data.data)) {
                const originalArgs = cacheEntry.originalArgs;
                const endpointName = cacheKey.startsWith('getInfiniteContacts') ? 'getInfiniteContacts' : 'getPaginatedContacts';

                const patchList = (contactsCoreApi.util.updateQueryData as unknown as DynamicPatchFn<{ data?: Contact[] }>)(endpointName, originalArgs, (cache) => {
                  if (cache?.data && Array.isArray(cache.data)) {
                    const idx = cache.data.findIndex((c: Contact) => c.id === id);
                    if (idx !== -1) {
                      cache.data[idx] = { ...cache.data[idx], isBlocked: false };
                    }
                  }
                });
                patches.push(dispatch(patchList));
              }
            }
          });
        }

        try {
          await queryFulfilled;
        } catch {
          // Revert all patches on error
          patches.forEach(p => p.undo());
        }
      },
      invalidatesTags: (result, error, id) => [
        { type: 'Contacts', id },
        { type: 'Contacts', id: 'LIST' },
        { type: 'Conversations', id: 'LIST' },
        { type: 'Conversations', id: 'INFINITE-LIST' }
      ]
    }),
  }),
  overrideExisting: true
});

export const {
  useGetAllContactsQuery,
  useLazyGetAllContactsQuery,
  useGetContactDetailsQuery,
  useCreateContactMutation,
  useSyncExternalContactsMutation,
  useUpdateContactMutation,
  useDeleteContactMutation,
  useGetContactsCountQuery,
  useGetPaginatedContactsQuery,
  useLazyGetPaginatedContactsQuery,
  useGetInfiniteContactsQuery,
  useGetContactByConversationQuery,
  useLazyGetContactByConversationQuery,
  useBlockContactMutation,
  useUnblockContactMutation,
  useChangeConsentMutation
} = contactsCoreApi;